import { FormDrawer, FormDrawerContent, Id, LaravelResponse, MultiSelectField } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TenantIndexResource, TenantOptionResource } from "../../../../shared/types/tenant";

interface Props {
	title: string;
	entityId: Id | null;
	type: "restore" | "archive";
	service: ITenantArchiveService;
	onClose: () => void;
	onSubmit: (values: TenantArchiveFormState) => Promise<boolean>;
}

export interface TenantArchiveFormState {
	tenants: TenantOptionResource[];
}

interface ITenantArchiveService {
	getTenantsKey: (id: Id) => string[];
	getTenants: (id: Id) => Promise<AxiosResponse<LaravelResponse<TenantIndexResource[]>, any>>;
	getArchivedTenantsKey: (id: Id) => string[];
	getArchivedTenants: (id: Id) => Promise<AxiosResponse<LaravelResponse<TenantIndexResource[]>, any>>;
}

export const TenantArchiveDrawer: FC<Props> = ({ entityId, service, type, ...props }) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: TenantArchiveFormState) => {
			const errors: { [K in keyof TenantArchiveFormState]?: any } = {};

			if (!values.tenants) {
				errors.tenants = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			{...props}
			open={entityId !== null}
			onClosed={() => void 0}
			initialValues={{ tenants: [] }}
			validate={validate}
			renderForm={(props) =>
				entityId !== null && (
					<FormDrawerContent {...props}>
						<CompanyTenantMultiSelectField
							name="tenants"
							label={t("Tenants")}
							entityId={entityId as Id}
							type={type}
							service={service}
						/>
					</FormDrawerContent>
				)
			}
		/>
	);
};

interface TenantMultiSelectFieldProps {
	name: string;
	label: string;
	entityId: Id;
	type: "restore" | "archive";
	service: ITenantArchiveService;
}

const CompanyTenantMultiSelectField: FC<TenantMultiSelectFieldProps> = ({ entityId, type, service, ...props }) => {
	const data = useMemo(() => {
		if (type === "archive") {
			return {
				queryKey: service.getTenantsKey(entityId),
				queryFn: () => service.getTenants(entityId).then((r) => r.data.data),
			};
		}
		return {
			queryKey: service.getArchivedTenantsKey(entityId),
			queryFn: () => service.getArchivedTenants(entityId).then((r) => r.data.data),
		};
	}, [entityId, service, type]);
	const { data: options = [] } = useQuery<TenantIndexResource[]>(data);

	return <MultiSelectField {...props} options={options} />;
};
